import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import type { MeDTO, UserDTO } from "@justone/shared";
import { errorMessage } from "../api/http";
import { isPending, useMarkRead, useMessages } from "../api/queries";
import { dayKey, formatDay } from "../lib/format";
import type { RoomView } from "../lib/room";
import { MessageRow } from "./MessageRow";
import { Icon, Skeleton } from "./ui";

const GROUP_MS = 5 * 60 * 1000;
const NEAR_BOTTOM_PX = 120;

interface Props {
  room: RoomView;
  users: Map<number, UserDTO>;
  me: MeDTO;
  /** 채널에 들어온 순간의 마지막 읽은 위치. "새 메시지" 구분선 위치를 정한다. */
  readMarker: number | null;
  onOpenThread: (rootId: number) => void;
  /** DM: 이 메시지부터 채널로 옮기기 */
  onMoveFrom?: (messageId: number) => void;
}

export function MessageList({ room, users, me, readMarker, onOpenThread, onMoveFrom }: Props) {
  const q = useMessages(room.ref);
  const markRead = useMarkRead();
  const scrollRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  const [unseenBelow, setUnseenBelow] = useState(0);

  const messages = useMemo(() => (q.data ? q.data.pages.slice().reverse().flatMap((p) => p.messages) : []), [q.data]);
  const firstId = messages[0]?.id;
  const last = messages.at(-1);
  const meNames = useMemo(() => new Set([me.username, me.displayName].filter((x): x is string => !!x).map((x) => x.toLowerCase())), [me]);

  // 스크롤 위치 관리: 처음엔 새 메시지 구분선(없으면 맨 아래), 위로 불러오면 위치 유지, 새 글은 아래에 붙을 때만 따라감
  const prev = useRef<{ firstId?: number; lastId?: number; height: number; top: number }>({ height: 0, top: 0 });
  const [params, setParams] = useSearchParams();
  const focusId = Number(params.get("m")) || null;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;
    const p = prev.current;
    if (p.lastId === undefined) {
      const divider = el.querySelector(".new-divider");
      const focus = focusId ? el.querySelector(`[data-message-id="${focusId}"]`) : null;
      if (focus) {
        focus.scrollIntoView({ block: "center" });
        focus.classList.add("highlight");
        setParams((sp) => (sp.delete("m"), sp), { replace: true });
      } else if (divider) divider.scrollIntoView({ block: "center" });
      else el.scrollTop = el.scrollHeight;
    } else if (firstId !== p.firstId && last?.id === p.lastId) {
      el.scrollTop = el.scrollHeight - p.height + p.top;
    } else if (last && last.id !== p.lastId) {
      if (nearBottom.current || last.authorId === me.id) el.scrollTop = el.scrollHeight;
      else setUnseenBelow((n) => n + 1);
    } else if (nearBottom.current && el.scrollHeight !== p.height) {
      // 맨 아래를 보던 중에 메시지가 길어지면 (링크 미리보기가 붙는 등) 계속 맨 아래
      el.scrollTop = el.scrollHeight;
    }
    prev.current = { firstId, lastId: last?.id, height: el.scrollHeight, top: el.scrollTop };
  }, [messages, firstId, last, me.id]);

  // 맨 위에 닿으면 이전 메시지
  useEffect(() => {
    const el = topRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && q.hasNextPage && !q.isFetchingNextPage) {
          const s = scrollRef.current;
          if (s) prev.current = { ...prev.current, height: s.scrollHeight, top: s.scrollTop };
          void q.fetchNextPage();
        }
      },
      { root: scrollRef.current, rootMargin: "200px 0px 0px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage]);

  // 화면에 보이고 맨 아래에 있으면 읽음 처리 (보내는 중인 내 메시지는 아직 id가 없으니 그 앞까지)
  const lastSent = [...messages].reverse().find((m) => !isPending(m));
  const tryMarkRead = () => {
    if (!lastSent || !room.tracksRead || document.visibilityState !== "visible" || !nearBottom.current) return;
    if ((room.lastReadMessageId ?? 0) >= lastSent.id && room.unread === 0) return;
    markRead.mutate({ room: room.ref, messageId: lastSent.id });
  };
  useEffect(() => {
    tryMarkRead();
    const onVis = () => tryMarkRead();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  });

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    prev.current = { ...prev.current, height: el.scrollHeight, top: el.scrollTop };
    if (nearBottom.current && unseenBelow) {
      setUnseenBelow(0);
      tryMarkRead();
    }
  };

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setUnseenBelow(0);
  };

  const firstUnread = readMarker === null ? undefined : messages.find((m) => m.id > readMarker && m.authorId !== me.id && !m.deleted);

  return (
    <div className="channel-view">
      <div className="message-scroll" ref={scrollRef} onScroll={onScroll} role="log" aria-label={`${room.title} 메시지`} aria-live="polite">
        <div ref={topRef} />
        {q.isFetchingNextPage && <div className="load-older">이전 메시지를 불러오는 중…</div>}
        {q.data && !q.hasNextPage && (
          <div className="channel-intro">
            <h2>{room.title}</h2>
            <p>{room.intro}</p>
          </div>
        )}
        {q.isPending && <Skeleton kind="messages" rows={6} />}
        {q.isError && <div className="load-older">메시지를 불러오지 못했습니다. {errorMessage(q.error)}</div>}
        {messages.map((m, i) => {
          const before = messages[i - 1];
          const newDay = !before || dayKey(new Date(before.createdAt)) !== dayKey(new Date(m.createdAt));
          const continued =
            !newDay &&
            before !== undefined &&
            before.kind === "user" &&
            m.kind === "user" &&
            before.authorId === m.authorId &&
            new Date(m.createdAt).getTime() - new Date(before.createdAt).getTime() < GROUP_MS &&
            m !== firstUnread;
          return (
            <Fragment key={m.id}>
              {newDay && (
                <div className="day-divider" role="separator">
                  {formatDay(m.createdAt)}
                </div>
              )}
              {m === firstUnread && (
                <div className="new-divider" role="separator">
                  새 메시지
                </div>
              )}
              <MessageRow message={m} users={users} me={me} continued={continued} meNames={meNames} onOpenThread={onOpenThread} onMoveFrom={onMoveFrom} />
            </Fragment>
          );
        })}
      </div>
      {unseenBelow > 0 && (
        <button type="button" className="btn btn-primary btn-sm jump-latest" onClick={jumpToLatest}>
          <Icon name="arrowDown" /> 새 메시지 {unseenBelow}개
        </button>
      )}
    </div>
  );
}
